import React, { useEffect, useState } from 'react';
import { PageList } from './PageList';
import { Toggle } from './ToggleContent';
import { ArrowDownwardOutlined, ArrowUpwardOutlined, CheckBoxOutlineBlank, SwapVertOutlined } from '@mui/icons-material';
import { ReactComponent as CheckBoxOutlined } from "assets/icons/check-box-outlined.svg";
import { ReactNode } from 'react';
import { Checkbox } from './LabelledCheckbox';

interface IDataColValidationSegments {
    splitter: string,
    min: number
}

export interface IKeyValuePair {
    key: any,
    value: any
}

export interface IDataColValidation {
    required: boolean,
    minLength: number,
    maxLength?: number,
    segments: IDataColValidationSegments,
    contains: string[],
    startsWith: string[],
    endsWith: string[],
    tip: string,
    values?: IKeyValuePair[],
    immediateValidationFn?: (value: string) => Promise<boolean>,
    immediateValidationMsgFn?: (value: string) => string
}

export interface IDataCol {
    // required
    name: string,
    heading: string,
    className: string,

    // optional
    clickAction?: string,
    gridHeading?: string,
    formatRow?: (row: any) => any,
    format?: (val: any) => any,
    sort?: (a: any, b: any, order: number) => number,
    filterable?: boolean,
    showInGrid?: number,
    formComponent?: any,
    placeholderText?: string,
    tip?: string,
    validation?: IDataColValidation
    readonly?: boolean,
    readonlyIf?: (row: any) => boolean,
    readonlyForEdit?: boolean,
    setValue?: (row: any, value: any) => void
}

export interface IDataGridProps {
    rows: any[],
    selectedItems?: any[],
    setSelectedItems?: (items: any[]) => void,
    columns: IDataCol[],
    onClick?: (row: any) => void,
    onDoubleClick?: (row: any) => void,
    selectMode?: "one" | "none" | "many",
    pageSize: number,
    sortCol?: IDataCol,
    nullValue?: ReactNode,
    perform?: (actionName: string, row: any) => void
}

interface IFilterValue {
    column: IDataCol,
    value: any
}

export const DataGrid = (props: IDataGridProps) => {
    const [filterValues, setFilterValues] = useState([] as IFilterValue[]);
    const [filteredRows, setFilteredRows] = useState(props.rows || []);
    const [pageNumber, setPageNumber] = useState(1);
    const [sortCol, setSortCol] = useState(props.sortCol ?? (props.columns && props.columns.length > 0 ? props.columns[0] : null));
    const [sortDirection, setSortDirection] = useState(1);

    useEffect(() => {
        toggleFilterFn();
    }, [props.rows]);

    const areAllSelected = () => {
        const selectedCount = (props.selectedItems ?? []).filter(sel => filteredRows.includes(sel)).length;

        switch (selectedCount) {
            case filteredRows.length:
                return true;
            case 0:
                return false;
        }

        return 'partial';
    };

    const selectAll = () => {
        if (props.setSelectedItems && props.selectMode === "many") {
            if (areAllSelected() === true) {
                props.setSelectedItems(
                    // selected, less the showing
                    (props.selectedItems ?? []).filter(sel => !filteredRows.includes(sel))
                );
            } else {
                props.setSelectedItems(
                    // selected, but not showing... plus the showing
                    (props.selectedItems ?? []).filter(sel => !filteredRows.includes(sel)).concat(filteredRows)
                );
            }
        }
    };

    const onClick = (row: any) => {
        if (!props) {
            return;
        }

        if (props.onClick) {
            props.onClick(row);
            return;
        }

        if (!props.setSelectedItems) {
            return;
        }

        switch (props.selectMode) {
            case "one":
                if (props.selectedItems?.includes(row)) {
                    return props.setSelectedItems([]);
                }
                else {
                    return props.setSelectedItems([row]);
                }
                
            case "many":
                if (props.selectedItems?.includes(row)) {
                    return props.setSelectedItems(props.selectedItems.filter(r => r !== row));
                }
                else {
                    return props.setSelectedItems((props.selectedItems ?? []).concat([row]));
                }
        }
    };

    const updateSort = (col: IDataCol) => {
        if (sortCol === col) {
            setSortDirection(sortDirection * -1);
        } else {
            setSortCol(col);
            setSortDirection(1);
        }
    };

    const colHeaderVal = (rawValue: any, col: IDataCol, blankValue?: any) => {
        if (rawValue === null) return blankValue;

        if (col.format) {
            return col.format(rawValue);
        }

        if (col.validation?.values) {
            return col.validation.values.find(v => v.key === rawValue)?.value ?? rawValue;
        }

        return rawValue;
    };

    const toggleFilterFn = async (col?: IDataCol, val?: string) => {
        var arr = col ? filterValues.filter(f => !(f.column.name === col.name && f.value === val)) : filterValues;

        if (col && val && arr.length == filterValues.length) {
            arr.push({ column: col, value: val });
        }

        setFilterValues(arr);

        var distinctColumnsFiltered = arr.map(f => f.column.name).filter((v, i, s) => { return s.indexOf(v) === i; }).length;

        setFilteredRows(props.rows.filter(
            row => arr.filter(
                fv => fv.value === colHeaderVal(row[fv.column.name], fv.column, "(Blanks)")
            ).length === distinctColumnsFiltered)
        );
    };

    const distinct = (rows: any[], col: IDataCol) => {
        return rows
            // first convert it to a string
            .map(row => colHeaderVal(row[col.name], col, "(Blanks)"))
            // then find the distinct list of strings
            .filter((rowVal, idx, acumulator) => { return acumulator.indexOf(rowVal) === idx; })
            .sort();
    };

    const colVal = (row: any, col: IDataCol) => {
        var rawValue = row[col.name];

        return colHeaderVal(rawValue, col);
    };

    const perform = (e: React.MouseEvent<HTMLTableCellElement, MouseEvent>, actionName: string, row: any) => {
        if (props.perform) {
            e.stopPropagation();
            props.perform(actionName, row);
        }
    };

    return (
        <>
            <div className="dg-container no-text-select">
                <table className="dg-table">
                    <tbody>
                        <tr className="dg-header">

                            {props.selectMode === "many" &&
                                <td className={"db-grid-select " + props.selectMode} onClick={selectAll}>
                                    <Checkbox checkedValue={areAllSelected()} />
                                </td>
                            }

                            {props.columns.map((col, idx) => {
                                if (col.filterable && props.rows?.length > 0) {
                                    const vals = distinct(props.rows, col);

                                    if (vals.length > 1) {
                                        return (
                                            <td colSpan={idx === 0 && props.selectMode === "one" ? 2 : 1} className={"dg-col dg-filter " + col.className + (filterValues.some(fv => fv.column.name === col.name) ? " filtered" : "")} key={col.name}>
                                                <Toggle label={col.gridHeading ?? col.heading} >
                                                    {vals.map(
                                                        val => {
                                                            return (
                                                                <div key={val} className="dg-filter-item" onClick={() => toggleFilterFn(col, val)}>
                                                                    {filterValues.findIndex(fv => fv.column.name === col.name && fv.value === val) === -1 ? <CheckBoxOutlineBlank /> : <CheckBoxOutlined />}
                                                                    {val}
                                                                </div>
                                                            );
                                                        }
                                                    )}
                                                </Toggle>
                                            </td>
                                        );
                                    } else {
                                        return (
                                            <td colSpan={idx === 0 && props.selectMode === "one" ? 2 : 1} className={"dg-col " + col.className} key={col.name}>
                                                {col.gridHeading ?? col.heading}
                                            </td>
                                        );
                                    }
                                } else {
                                    if (col.sort) {
                                        return (
                                            <td colSpan={idx === 0 && props.selectMode === "one" ? 2 : 1} onClick={() => updateSort(col)} className={"dg-col " + col.className} key={col.name}>
                                                {col.gridHeading ?? col.heading} {(sortCol !== col ? <SwapVertOutlined /> : (sortDirection === 1 ? <ArrowDownwardOutlined /> : <ArrowUpwardOutlined />))}
                                            </td>
                                        );
                                    } else {
                                        return (
                                            <td colSpan={idx === 0 && props.selectMode === "one" ? 2 : 1} className={"dg-col " + col.className} key={col.name}>
                                                {col.gridHeading ?? col.heading}
                                            </td>
                                        );
                                    }
                                }
                            })}
                            <td className="dg-col-filler"></td>
                        </tr>

                        <tr><td colSpan={props.columns.length + 2} className="dg-post-header-row"></td></tr>

                        {filteredRows.sort((a, b) => (sortCol && sortCol.sort) ? sortCol.sort(a, b, sortDirection) : sortDirection)
                            .filter((r, i) => { return props.pageSize < 1 || (i >= props.pageSize * (pageNumber - 1) && i < props.pageSize * pageNumber) })
                            .map((row, rowIndex) =>
                                <tr key={rowIndex}
                                    className="dg-row"
                                    onDoubleClick={() => { if (props.onDoubleClick) props.onDoubleClick(row) }}
                                    onClick={() => onClick(row)}>

                                    {props.selectMode !== "none" &&
                                        <td className={"db-grid-select " + props.selectMode}>
                                            {props.selectedItems?.indexOf(row) === -1
                                                ? <CheckBoxOutlineBlank />
                                                : <CheckBoxOutlined />
                                            }
                                        </td>}

                                    {props.columns.map(col => {
                                        if (!props.nullValue || row[col.name] !== null) {
                                            return (
                                                <td
                                                    className={"dg-col " + col.className + (col.clickAction ? " dg-col-action" : "")}
                                                    onClick={e => ((props.perform && col.clickAction) ? perform(e, col.clickAction, row) : null)}
                                                    key={col.name} >
                                                    {colVal(row, col)}
                                                </td>
                                            );
                                        } else {
                                            return (
                                                <td className={"dg-col dg-null-col " + col.className} key={col.name}>{props.nullValue}</td>
                                            );
                                        }
                                    })}
                                    <td className="dg-col-filler"></td>
                                </tr>
                            )}
                    </tbody>
                </table>
            </div>

            {
                props.pageSize > 0 && filteredRows.length > props.pageSize &&
                <div className="dg-page-list">
                    <PageList pageNumber={pageNumber} pageSize={props.pageSize} totalResults={filteredRows.length} moveToPage={setPageNumber} />
                </div>
            }
        </>
    );
};